import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  setDoc,
  limit
} from 'firebase/firestore';
import { db } from './config';

// Collection names
const COLLECTIONS = {
  PRODUCTS: 'products',
  CATEGORIES: 'categories',
  HERO_SLIDES: 'heroSlides',
  TESTIMONIALS: 'testimonials',
  COLLECTION_IMAGES: 'collectionImages',
  SETTINGS: 'settings',
  MEDIA_BANK: 'mediaBank'
};

// ============= PRODUCTS =============
export const getProducts = async () => {
  try {
    const q = query(collection(db, COLLECTIONS.PRODUCTS), orderBy('id'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ ...doc.data(), firebaseId: doc.id }));
  } catch (error) {
    console.error('Error getting products:', error);
    return [];
  }
};

export const getProductById = async (id) => {
  try {
    const q = query(collection(db, COLLECTIONS.PRODUCTS));
    const querySnapshot = await getDocs(q);
    const products = querySnapshot.docs.map(doc => ({ ...doc.data(), firebaseId: doc.id }));
    return products.find(p => p.id === id);
  } catch (error) {
    console.error('Error getting product:', error);
    return null;
  }
};

export const addProduct = async (productData) => {
  try {
    const products = await getProducts();
    const maxId = Math.max(...products.map(p => p.id), 0);
    const newProduct = {
      ...productData,
      id: maxId + 1,
      createdAt: new Date().toISOString()
    };
    const docRef = await addDoc(collection(db, COLLECTIONS.PRODUCTS), newProduct);
    return { ...newProduct, firebaseId: docRef.id };
  } catch (error) {
    console.error('Error adding product:', error);
    throw error;
  }
};

export const updateProduct = async (id, updates) => {
  try {
    const q = query(collection(db, COLLECTIONS.PRODUCTS));
    const querySnapshot = await getDocs(q);
    const productDoc = querySnapshot.docs.find(doc => doc.data().id === id);

    if (productDoc) {
      await updateDoc(doc(db, COLLECTIONS.PRODUCTS, productDoc.id), {
        ...updates,
        updatedAt: new Date().toISOString()
      });
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error updating product:', error);
    throw error;
  }
};

export const deleteProduct = async (id) => {
  try {
    const q = query(collection(db, COLLECTIONS.PRODUCTS));
    const querySnapshot = await getDocs(q);
    const productDoc = querySnapshot.docs.find(doc => doc.data().id === id);

    if (productDoc) {
      await deleteDoc(doc(db, COLLECTIONS.PRODUCTS, productDoc.id));
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error deleting product:', error);
    throw error;
  }
};

// ============= CATEGORIES =============
export const getCategories = async () => {
  try {
    const q = query(collection(db, COLLECTIONS.CATEGORIES), orderBy('id'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ ...doc.data(), firebaseId: doc.id }));
  } catch (error) {
    console.error('Error getting categories:', error);
    return [];
  }
};

export const addCategory = async (categoryData) => {
  try {
    const categories = await getCategories();
    const maxId = Math.max(...categories.map(c => c.id), 0);
    const newCategory = {
      ...categoryData,
      id: maxId + 1
    };
    const docRef = await addDoc(collection(db, COLLECTIONS.CATEGORIES), newCategory);
    return { ...newCategory, firebaseId: docRef.id };
  } catch (error) {
    console.error('Error adding category:', error);
    throw error;
  }
};

export const updateCategory = async (id, updates) => {
  try {
    const q = query(collection(db, COLLECTIONS.CATEGORIES));
    const querySnapshot = await getDocs(q);
    const categoryDoc = querySnapshot.docs.find(doc => doc.data().id === id);

    if (categoryDoc) {
      await updateDoc(doc(db, COLLECTIONS.CATEGORIES, categoryDoc.id), updates);
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error updating category:', error);
    throw error;
  }
};

export const deleteCategory = async (id) => {
  try {
    const q = query(collection(db, COLLECTIONS.CATEGORIES));
    const querySnapshot = await getDocs(q);
    const categoryDoc = querySnapshot.docs.find(doc => doc.data().id === id);

    if (categoryDoc) {
      await deleteDoc(doc(db, COLLECTIONS.CATEGORIES, categoryDoc.id));
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error deleting category:', error);
    throw error;
  }
};

// ============= HERO SLIDES =============
export const getHeroSlides = async () => {
  try {
    const q = query(collection(db, COLLECTIONS.HERO_SLIDES), orderBy('id'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ ...doc.data(), firebaseId: doc.id }));
  } catch (error) {
    console.error('Error getting hero slides:', error);
    return [];
  }
};

export const addHeroSlide = async (slideData) => {
  try {
    const slides = await getHeroSlides();
    const maxId = Math.max(...slides.map(s => s.id), 0);
    const newSlide = {
      ...slideData,
      id: maxId + 1,
      active: slideData.active !== undefined ? slideData.active : true
    };
    const docRef = await addDoc(collection(db, COLLECTIONS.HERO_SLIDES), newSlide);
    return { ...newSlide, firebaseId: docRef.id };
  } catch (error) {
    console.error('Error adding hero slide:', error);
    throw error;
  }
};

export const updateHeroSlide = async (id, updates) => {
  try {
    const q = query(collection(db, COLLECTIONS.HERO_SLIDES));
    const querySnapshot = await getDocs(q);
    const slideDoc = querySnapshot.docs.find(doc => doc.data().id === id);

    if (slideDoc) {
      await updateDoc(doc(db, COLLECTIONS.HERO_SLIDES, slideDoc.id), updates);
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error updating hero slide:', error);
    throw error;
  }
};

export const deleteHeroSlide = async (id) => {
  try {
    const q = query(collection(db, COLLECTIONS.HERO_SLIDES));
    const querySnapshot = await getDocs(q);
    const slideDoc = querySnapshot.docs.find(doc => doc.data().id === id);

    if (slideDoc) {
      await deleteDoc(doc(db, COLLECTIONS.HERO_SLIDES, slideDoc.id));
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error deleting hero slide:', error);
    throw error;
  }
};

// ============= TESTIMONIALS =============
export const getTestimonials = async () => {
  try {
    const q = query(collection(db, COLLECTIONS.TESTIMONIALS), orderBy('id'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ ...doc.data(), firebaseId: doc.id }));
  } catch (error) {
    console.error('Error getting testimonials:', error);
    return [];
  }
};

export const addTestimonial = async (testimonialData) => {
  try {
    const testimonials = await getTestimonials();
    const maxId = Math.max(...testimonials.map(t => t.id), 0);
    const newTestimonial = {
      ...testimonialData,
      id: maxId + 1
    };
    const docRef = await addDoc(collection(db, COLLECTIONS.TESTIMONIALS), newTestimonial);
    return { ...newTestimonial, firebaseId: docRef.id };
  } catch (error) {
    console.error('Error adding testimonial:', error);
    throw error;
  }
};

export const updateTestimonial = async (id, updates) => {
  try {
    const q = query(collection(db, COLLECTIONS.TESTIMONIALS));
    const querySnapshot = await getDocs(q);
    const testimonialDoc = querySnapshot.docs.find(doc => doc.data().id === id);

    if (testimonialDoc) {
      await updateDoc(doc(db, COLLECTIONS.TESTIMONIALS, testimonialDoc.id), updates);
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error updating testimonial:', error);
    throw error;
  }
};

export const deleteTestimonial = async (id) => {
  try {
    const q = query(collection(db, COLLECTIONS.TESTIMONIALS));
    const querySnapshot = await getDocs(q);
    const testimonialDoc = querySnapshot.docs.find(doc => doc.data().id === id);

    if (testimonialDoc) {
      await deleteDoc(doc(db, COLLECTIONS.TESTIMONIALS, testimonialDoc.id));
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error deleting testimonial:', error);
    throw error;
  }
};

// ============= COLLECTION IMAGES =============
export const getCollectionImages = async () => {
  try {
    const q = query(collection(db, COLLECTIONS.COLLECTION_IMAGES), orderBy('id'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ ...doc.data(), firebaseId: doc.id }));
  } catch (error) {
    console.error('Error getting collection images:', error);
    return [];
  }
};

export const updateCollectionImage = async (id, updates) => {
  try {
    const q = query(collection(db, COLLECTIONS.COLLECTION_IMAGES));
    const querySnapshot = await getDocs(q);
    const collectionDoc = querySnapshot.docs.find(doc => doc.data().id === id);

    if (collectionDoc) {
      await updateDoc(doc(db, COLLECTIONS.COLLECTION_IMAGES, collectionDoc.id), updates);
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error updating collection image:', error);
    throw error;
  }
};

// ============= SETTINGS =============
export const getSettings = async () => {
  try {
    const docRef = doc(db, COLLECTIONS.SETTINGS, 'store');
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return docSnap.data();
    }
    return null;
  } catch (error) {
    console.error('Error getting settings:', error);
    return null;
  }
};

export const updateSettings = async (settings) => {
  try {
    const docRef = doc(db, COLLECTIONS.SETTINGS, 'store');
    await setDoc(docRef, settings, { merge: true });
    return true;
  } catch (error) {
    console.error('Error updating settings:', error);
    throw error;
  }
};

// ============= CHECK INITIALIZATION =============
export const checkInitialized = async () => {
  try {
    const docRef = doc(db, COLLECTIONS.SETTINGS, 'store');
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return data.initialized === true;
    }
    return false;
  } catch (error) {
    console.error('Error checking initialization:', error);
    return false;
  }
};

// ============= INITIALIZE DATA =============
export const initializeFirebaseData = async (initialData) => {
  try {
    console.log('Checking if Firebase needs initialization...');

    // Check if already initialized using settings flag
    const isInitialized = await checkInitialized();
    if (isInitialized) {
      console.log('✅ Firebase already initialized');
      return false;
    }

    console.log('🔄 Initializing Firebase with data...');

    // Add products
    console.log('Adding products...');
    for (const product of initialData.products) {
      await addDoc(collection(db, COLLECTIONS.PRODUCTS), product);
    }

    // Add categories
    console.log('Adding categories...');
    for (const category of initialData.categories) {
      await addDoc(collection(db, COLLECTIONS.CATEGORIES), category);
    }

    // Add hero slides
    console.log('Adding hero slides...');
    for (const slide of initialData.heroSlides) {
      await addDoc(collection(db, COLLECTIONS.HERO_SLIDES), slide);
    }

    // Add testimonials
    console.log('Adding testimonials...');
    for (const testimonial of initialData.testimonials) {
      await addDoc(collection(db, COLLECTIONS.TESTIMONIALS), testimonial);
    }

    // Add collection images
    console.log('Adding collection images...');
    for (const collectionImage of initialData.collectionImages) {
      await addDoc(collection(db, COLLECTIONS.COLLECTION_IMAGES), collectionImage);
    }

    // Add settings with initialized flag
    console.log('Adding settings...');
    await setDoc(doc(db, COLLECTIONS.SETTINGS, 'store'), {
      ...initialData.settings,
      initialized: true,
      initializedAt: new Date().toISOString()
    });

    console.log('✅ Firebase data initialized successfully!');
    return true;
  } catch (error) {
    console.error('❌ Error initializing Firebase data:', error);
    throw error;
  }
};

// ============= MEDIA BANK =============
export const getMediaBankImages = async (searchTerm = '', pageSize = 20, startAfter = null) => {
  try {
    let q = query(
      collection(db, COLLECTIONS.MEDIA_BANK),
      orderBy('createdAt', 'desc')
    );

    // Apply limit
    if (pageSize) {
      q = query(q, limit(pageSize));
    }

    const querySnapshot = await getDocs(q);
    const images = querySnapshot.docs.map(doc => ({
      ...doc.data(),
      firebaseId: doc.id
    }));

    // Filter by search term (client-side for now)
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      return images.filter(img =>
        img.name?.toLowerCase().includes(lowerSearch) ||
        img.description?.toLowerCase().includes(lowerSearch)
      );
    }

    return images;
  } catch (error) {
    console.error('Error getting media bank images:', error);
    return [];
  }
};

export const addMediaBankImage = async (imageData) => {
  try {
    const newImage = {
      ...imageData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    const docRef = await addDoc(collection(db, COLLECTIONS.MEDIA_BANK), newImage);
    return { ...newImage, firebaseId: docRef.id };
  } catch (error) {
    console.error('Error adding media bank image:', error);
    throw error;
  }
};

export const updateMediaBankImage = async (firebaseId, updates) => {
  try {
    await updateDoc(doc(db, COLLECTIONS.MEDIA_BANK, firebaseId), {
      ...updates,
      updatedAt: new Date().toISOString()
    });
    return true;
  } catch (error) {
    console.error('Error updating media bank image:', error);
    throw error;
  }
};

export const deleteMediaBankImage = async (firebaseId) => {
  try {
    await deleteDoc(doc(db, COLLECTIONS.MEDIA_BANK, firebaseId));
    return true;
  } catch (error) {
    console.error('Error deleting media bank image:', error);
    throw error;
  }
};

export const getMediaBankImageByUrl = async (url) => {
  try {
    const q = query(collection(db, COLLECTIONS.MEDIA_BANK));
    const querySnapshot = await getDocs(q);
    const images = querySnapshot.docs.map(doc => ({ ...doc.data(), firebaseId: doc.id }));
    return images.find(img => img.url === url);
  } catch (error) {
    console.error('Error getting media bank image by URL:', error);
    return null;
  }
};
